import { DownloadFileIcon } from "@/icons/DownloadFileIcon";
import { FullScreenIcon } from "@/icons/FullScreenIcon";
import { FiMinus, FiPlus } from "react-icons/fi";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./css/DocView.module.css";
import { FileData, TaskData } from "@/types";
import * as pdfjsLib from "pdfjs-dist";
import { langDirection } from "@/utils/languages";
import { LoadingPageAnimation } from "../addons/LoadingPageAnimation";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

interface Props {
  file: FileData;
}

export const DocView: React.FC<Props> = ({ file }) => {
  const { t, i18n } = useTranslation("PdfView");
  const direction = langDirection(i18n.language);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [renderedContent, setRenderedContent] = useState<JSX.Element | null>(null);

  function zoomIn() {
    setZoomLevel((prev) => Math.min(prev + 10, 200));
  }

  function zoomOut() {
    setZoomLevel((prev) => Math.max(prev - 10, 100));
  }

  useEffect(() => {
    const renderFile = async () => {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      const show_path = undefined;

      if (fileExtension === "pdf" || show_path) {
        await renderPDF();
      } else if (fileExtension === "docx" || fileExtension === "doc") {
        await renderDOCX();
      } else if (fileExtension === "csv") {
        await renderCSV();
      } else {
        setError(t("unsupportedFileType"));
        setIsLoading(false);
      }
    };

    const renderPDF = async () => {
      try {
        const url = file.url;
        const loadingTask = pdfjsLib.getDocument(url);
        const pdf = await loadingTask.promise;

        const pages = [];
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
          const page = await pdf.getPage(pageNumber);
          const viewport = page.getViewport({ scale: 1.5 });

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d", { alpha: false });

          if (context) {
            canvas.width = viewport.width;
            canvas.height = viewport.height;

            const renderContext = {
              canvasContext: context,
              viewport,
            };

            await page.render(renderContext).promise;

            pages.push(
              <div key={pageNumber} className={styles.page}>
                <canvas
                  width={canvas.width}
                  height={canvas.height}
                  ref={(el) => {
                    if (el) {
                      const ctx = el.getContext("2d");
                      if (ctx) {
                        ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
                      }
                    }
                  }}
                  style={{
                    width: `${zoomLevel}%`,
                    height: "auto",
                    transition: "transform 0.2s ease-in-out",
                  }}
                />
              </div>
            );
          }
        }

        setRenderedContent(<div>{pages}</div>);
      } catch (error) {
        console.error("Error rendering PDF");
        setError(t("errorLoadingFile"));
      }
      setIsLoading(false);
    };

    const renderDOCX = async () => {
      try {
        const microsoftViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`;

        setRenderedContent(
          <div className={styles.docxContainer}>
            <iframe
              src={microsoftViewerUrl}
              width="100%"
              height="800px"
              frameBorder="0"
              className={styles.docxIframe}
              tabIndex={-1}
            />
          </div>
        );
      } catch (error) {
        console.error("Error rendering DOCX");
        setError(t("errorLoadingFile"));
      }
      setIsLoading(false);
    };

    const renderCSV = () => {
      try {
        const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(file.url)}&embedded=true`;

        setRenderedContent(
          <div className={styles.csvContainer}>
            <iframe
              src={googleViewerUrl}
              width="100%"
              height="765px"
              frameBorder="0"
              className={styles.csvIframe}
              tabIndex={-1}
            />
          </div>
        );
      } catch (error) {
        console.error("Error rendering CSV");
        setError(t("errorLoadingFile"));
      }
      setIsLoading(false);
    };

    renderFile();
  }, [file.url, file, zoomLevel]);

  return (
    <div className={styles.container}>
      <div className={styles.pdfViewContainer}>
        <div className={styles.pdfView}>
          <div ref={containerRef} className={styles.fileContainer} dir="ltr">
            {isLoading && (
              <div className={styles.errorContainer}>
                <LoadingPageAnimation />
              </div>
            )}
            {error && (
              <div className={styles.errorContainer}>
                <p>{t("errorLoadingFile")}</p>
              </div>
            )}
            {!isLoading && !error && renderedContent}
          </div>
        </div>
      </div>
    </div>
  );
};
